// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {openDialog} from 'Store/actions/dialogs';
import {useDeepMemo} from 'Utils/react-utils';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import {alphaNumericCompare} from 'Ui/models/maintenance';
import {MOWING_DIRECTIONS_ORDER} from 'Ui/components/settings/constants/mowing-directions';
import dialogTypes from 'Ui/components/common/dialog-types';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';

function directionsSortMethod(direction1, direction2) {
    const directionOrder1 = MOWING_DIRECTIONS_ORDER[direction1];
    const directionOrder2 = MOWING_DIRECTIONS_ORDER[direction2];

    if (
        !isNullOrUndefined(directionOrder1) &&
        !isNullOrUndefined(directionOrder2)
    ) {
        return directionOrder1 - directionOrder2;
    }

    return alphaNumericCompare(direction1, direction2);
}

function getDataGridColumns(openMowingDirectionDialog, translations) {
    return [
        {
            headerName: translations.ONLINK_MOWING_DIRECTION,
            field: 'direction',
            sortComparator: directionsSortMethod,
            renderCell: (params) => {
                const {
                    value, row
                } = params;

                return (
                    <EditLink
                        onClick={() =>
                            openMowingDirectionDialog({
                                mowingDirectionIndex: row.index
                            })
                        }
                    >
                        {translations[value] || value}
                    </EditLink>
                );
            },
            width: 230
        },
        {
            headerName: translations.ONLINK_APPLIES_TO,
            flex: 1,
            field: 'areasApplied'
        },
        {
            headerName: translations.STATUS,
            field: 'inactive',
            renderCell: (params) => {
                const {value} = params;

                return value ?
                    translations.inactive :
                    translations['EDIT_PDA_PAGE.LABEL.ACTIVE'];
            },
            width: 100
        }
    ];
}

function MowingDirectionsSettings(props) {
    const {
        membership, openMowingDirectionDialog, translations
    } =
        props;

    const mowingDirections = useDeepMemo(
        () =>
            membership.mowingDirections.map((mowingDirection, index) => ({
                ...mowingDirection,
                index,
                areasApplied: mowingDirection.areasApplied.join(', ')
            })),
        [membership.mowingDirections]
    );

    return (
        <div className='settings-container data-grid'>
            <DataGridWrapper
                columns={getDataGridColumns(
                    openMowingDirectionDialog,
                    translations
                )}
                getRowId={(row) => row.direction}
                initialState={{
                    sorting: {
                        sortModel: [{
                            field: 'direction',
                            sort: 'asc'
                        }]
                    }
                }}
                rows={mowingDirections}
                searchFields={['direction', 'areasApplied']}
                searchable={true}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

MowingDirectionsSettings.propTypes = {
    membership: PropTypes.membership,
    openMowingDirectionDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openMowingDirectionDialog(props) {
            dispatch(
                openDialog(dialogTypes.EDIT_MOWING_DIRECTION_DIALOG, props)
            );
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MowingDirectionsSettings);
